import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { User, Mail, Phone, Store, MessageSquare, Calendar, Save } from 'lucide-react';
import { usePostApiWorkspacesWorkspaceIdCustomers, usePutApiWorkspacesWorkspaceIdCustomersCustomerId, getGetApiWorkspacesWorkspaceIdCustomersQueryKey } from '@/api/generated/customers/customers';
import { useGetApiWorkspacesWorkspaceIdStores } from '@/api/generated/stores/stores';
import { useQueryClient } from '@tanstack/react-query';
import { extractStoresArray } from '@/lib/utils';
import { toast } from 'sonner';
import type { CustomerInfo, StoreItem } from '@/types';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';

interface CustomerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer?: CustomerInfo | null;
  mode?: 'add' | 'edit';
  onSave?: (customer: Partial<CustomerInfo>) => void;
}

export const CustomerModal: React.FC<CustomerModalProps> = ({
  open,
  onOpenChange,
  customer,
  mode = 'edit',
  onSave,
}) => {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceStore();
  const { data: stores } = useGetApiWorkspacesWorkspaceIdStores(activeWorkspaceId!, {
    query: { enabled: !!activeWorkspaceId },
  });
  const storesList = useMemo(() => extractStoresArray(stores) as StoreItem[], [stores]);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [storeId, setStoreId] = useState('');

  const createCustomerMutation = usePostApiWorkspacesWorkspaceIdCustomers();
  const updateCustomerMutation = usePutApiWorkspacesWorkspaceIdCustomersCustomerId();

  useEffect(() => {
    if (customer && mode === 'edit') {
      setName(customer.customerName || customer.name || '');
      setEmail(customer.customerEmail || customer.email || '');
      setPhone(customer.customerPhone || customer.phone || '');
      setStoreId(customer.storeId || (storesList[0]?.id ?? ''));
    } else if (mode === 'add') {
      setName('');
      setEmail('');
      setPhone('');
      setStoreId(storesList[0]?.id ?? '');
    }
  }, [customer, mode, storesList]);

  const isSubmitting = createCustomerMutation.isPending || updateCustomerMutation.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('O nome do cliente é obrigatório');
      return;
    }
    if (!email.trim() && !phone.trim()) {
      toast.error('Por favor, forneça pelo menos um email ou número de telefone');
      return;
    }
    if (!activeWorkspaceId) {
      toast.error('Nenhum workspace selecionado');
      return;
    }

    try {
      if (mode === 'add') {
        const created = await createCustomerMutation.mutateAsync({
          workspaceId: activeWorkspaceId,
          data: {
            storeId: storeId || storesList[0]?.id || '',
            name: name.trim() || undefined,
            email: email.trim() || undefined,
            phone: phone.trim() || undefined,
          },
        }) as any;

        const payload: Partial<CustomerInfo> = {
          id: created?.id || `cust-${Date.now()}`,
          name: name.trim(),
          customerName: name.trim(),
          email: email.trim() || null,
          customerEmail: email.trim() || null,
          phone: phone.trim() || null,
          customerPhone: phone.trim() || null,
          storeId: storeId || undefined,
          totalConversations: 0,
          lastActiveAt: new Date().toISOString(),
        };
        onSave?.(payload);
        toast.success(`Cliente "${name}" criado com sucesso`);
      } else if (customer?.id) {
        await updateCustomerMutation.mutateAsync({
          workspaceId: activeWorkspaceId,
          customerId: customer.id,
          data: {
            name: name.trim() || undefined,
            email: email.trim() || undefined,
            phone: phone.trim() || undefined,
          },
        });

        const payload: Partial<CustomerInfo> = {
          id: customer.id,
          name: name.trim(),
          customerName: name.trim(),
          email: email.trim() || null,
          customerEmail: email.trim() || null,
          phone: phone.trim() || null,
          customerPhone: phone.trim() || null,
          storeId: storeId || undefined,
          totalConversations: customer.totalConversations || 0,
          lastActiveAt: customer.lastActiveAt || new Date().toISOString(),
        };
        onSave?.(payload);
        toast.success(`Perfil do cliente "${name}" atualizado`);
      }

      queryClient.invalidateQueries({ queryKey: getGetApiWorkspacesWorkspaceIdCustomersQueryKey(activeWorkspaceId) });
      onOpenChange(false);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Falha ao salvar o cliente';
      toast.error(msg);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] p-6">
        <DialogHeader className="space-y-1.5 pb-2">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <User className="h-4 w-4" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold">
                {mode === 'add' ? 'Adicionar Novo Cliente' : 'Dados do Cliente'}
              </DialogTitle>
              <DialogDescription className="text-xs">
                {mode === 'add'
                  ? 'Cadastre um cliente no CRM da sua loja'
                  : 'Visualize e atualize os dados do cliente no CRM'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="customerName" className="text-xs font-semibold">
              Nome Completo do Cliente
            </Label>
            <div className="relative">
              <User className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="customerName"
                placeholder="ex: Maria Silva"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="pl-8 h-9 text-xs"
                required
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="customerEmail" className="text-xs font-semibold">
              Endereço de Email
            </Label>
            <div className="relative">
              <Mail className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="customerEmail"
                type="email"
                placeholder="ex: maria@exemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-8 h-9 text-xs"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="customerPhone" className="text-xs font-semibold">
              Número de Telefone
            </Label>
            <div className="relative">
              <Phone className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="customerPhone"
                placeholder="ex: +55 11 99999-9999"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="pl-8 h-9 text-xs"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="customerStore" className="text-xs font-semibold">
              Loja Associada
            </Label>
            <Select value={storeId} onValueChange={setStoreId}>
              <SelectTrigger id="customerStore" className="h-9 text-xs">
                <div className="flex items-center gap-2">
                  <Store className="h-3.5 w-3.5 text-muted-foreground" />
                  <SelectValue placeholder="Selecionar Loja" />
                </div>
              </SelectTrigger>
              <SelectContent>
                {storesList.map((s) => (
                  <SelectItem key={s.id} value={s.id} className="text-xs">
                    {s.name} ({s.provider})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {mode === 'edit' && customer && (
            <div className="grid grid-cols-2 gap-3 pt-2 p-3 rounded-lg bg-muted/40 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-primary/70" />
                <span>
                  <strong className="text-foreground">{customer.totalConversations || 0}</strong> conversas
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-primary/70" />
                <span>
                  Última atividade: <strong className="text-foreground">
                    {customer.lastActiveAt
                      ? new Date(customer.lastActiveAt).toLocaleDateString()
                      : 'N/D'}
                  </strong>
                </span>
              </div>
            </div>
          )}

          <DialogFooter className="pt-3 border-t border-border/50">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="text-xs"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={isSubmitting}
              className="gap-1.5 text-xs shadow-sm font-medium"
            >
              <Save className="h-3.5 w-3.5" />
              <span>{isSubmitting ? 'Salvando...' : mode === 'add' ? 'Criar Cliente' : 'Salvar Alterações'}</span>
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
